import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import * as layoutActions from '../../../core/store/layout/layout.actions';
import * as assetImportActions from '../../../core/store/asset-imports/asset-imports.actions';
import { CustomStudioFieldName, Franchise, AssetImports, User, Division } from '../../../core/store/models';
import { assetImportSelectors, rootSelectors as fromRoot } from '../../../core/store/selectors';
import * as userActions from '../../../core/store/users/user.actions';
import { Moment, sosMoment } from '../../services/moment.service';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import * as navActions from '../../../core/store/nav/nav.actions';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'ah-asset-import-history',
    templateUrl: './asset-import-history.component.html',
    standalone: false,
    styles: `
    :host ::ng-deep .p-datatable .p-paginator {
        padding: 1em;
        margin: 0 !important;
    }`
})
export class AssetImportHistoryComponent implements OnDestroy, OnInit {
    currentStudioId: number;
    customFieldNames: CustomStudioFieldName;
    deleting = false;
    assetImports: AssetImports[] = [];
    franchise: Franchise;
    division: Division;
    sub: Subscription;
    rowsPerPage = 50;
    userList: User[];
    userLookup: { [userId: number]: User } = {};

    moment: Moment;

    constructor(public dialog: MatDialog, private route: ActivatedRoute, private store: Store<fromRoot.State>) { }

    ngOnInit() {
        this.moment = sosMoment;
        const { selectedFranchise, selectedDivision } = this.route.parent.snapshot.data;
        this.franchise = selectedFranchise;
        this.division = selectedDivision;
        if (this.franchise) {
            this.currentStudioId = this.franchise.studio_id;

            this.store.dispatch(new userActions.ListByFranchiseAction(this.franchise));
            this.store.dispatch(new userActions.ListByStudioAction(this.franchise.studio_id));
            this.store.dispatch(new assetImportActions.ListByFranchiseAction(this.franchise.id));
        }
        else if (this.division) {
            this.currentStudioId = this.division.studio_id;

            this.store.dispatch(new userActions.ListByDivisionAction(this.division));
            this.store.dispatch(new userActions.ListByStudioAction(this.division.studio_id));
            this.store.dispatch(new assetImportActions.ListByDivisionAction(this.division.id));
        }
        this.setupSubscriptions();
    }

    ngOnDestroy() {
        this.sub.unsubscribe();
    }

    removeAssetImport(assetImports: AssetImports) {
        this.dialog.open(ConfirmModalComponent, {
            data: {
                textStyle: { 'white-space': 'pre-wrap' },
                title: 'Confirm Delete',
                mode: 'confirmDeleteAssetImports',
                confirmButton: 'Delete',
                confirmColor: 'warn'
            }
        }).afterClosed().pipe(take(1)).subscribe(del => {
            if (del) {
                this.deleting = true;
                this.store.dispatch(new assetImportActions.DeleteAction(assetImports));
                this.store.select(fromRoot.getAssetImportDeleteComplete).pipe(filter(complete => complete), take(1)).subscribe(() => {
                    this.deleting = false;
                    this.store.dispatch(new layoutActions.InfoAction({ message: `The asset import has been deleted.` }));
                    if (this.franchise) {
                        this.store.dispatch(new navActions.ListStatusAction({ studioId: this.franchise.studio_id, franchiseId: this.franchise.id }));
                        this.store.dispatch(new assetImportActions.ListByFranchiseAction(this.franchise.id));
                    }
                    else if (this.division) {
                        this.store.dispatch(new navActions.ListStatusAction({ studioId: this.division.studio_id, divisionId: this.division.id }));
                        this.store.dispatch(new assetImportActions.ListByDivisionAction(this.division.id));
                    }
                });
                this.store.select(fromRoot.getAssetImportDeleteFailed).pipe(filter(failed => failed), take(1)).subscribe(() => {
                    this.deleting = false;
                    this.store.dispatch(new layoutActions.ErrorAction({
                        error: new HttpErrorResponse({ error: new Error('The asset import delete failed.') })
                    }));
                });
            }
        });
    }

    showPaginator(assetImports: AssetImports[]) {
        return assetImports && assetImports.length && assetImports.length > this.rowsPerPage;
    }

    private setupSubscriptions() {
        this.sub = new Subscription();
        if (this.franchise) {
            const assetImportsSub = this.store.select(assetImportSelectors.getAssetImportsForSelectedFranchise).subscribe((imports) => {
                this.assetImports = imports;
            });
            this.sub.add(assetImportsSub);
        } else if (this.division) {
            const assetImportsSub = this.store.select(assetImportSelectors.getAssetImportsForSelectedDivision).subscribe((imports) => {
                this.assetImports = imports;
            });
            this.sub.add(assetImportsSub);
        }
        const usersSub = this.store.select(fromRoot.getAllUsers).subscribe((users) => {
            this.userLookup = users;
        });
        this.sub.add(usersSub);
    }
}
